import type { DataIndexItemKind } from '../utils/types';

/**
 * The filter that finds everyone, or everything, an item applies to.
 *
 * The number the details panel shows and the list the link opens have to
 * agree, so both are asked for with the same filter. It is the people list's
 * own encoding, which is what lets the link land on a list already narrowed.
 */

/** The `named_bitmap` type the backend gives a message or event condition. */
const MESSAGE_BITMAP_TYPE = 3;

/** The prefix an event carries in a bitmap name (`shorten.go`). */
const EVENT_BITMAP_PREFIX = 'e';

/** Base64 of the UTF-8 bytes, which is what the backend reads. */
function bytesToBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  return btoa(Array.from(bytes, (byte) => String.fromCodePoint(byte)).join(''));
}

/**
 * One condition in the people list's filter language. The two conditions
 * here are the only ones this page writes, so the shape is theirs rather
 * than the whole grammar's.
 */
type CountCondition =
  | { named_bitmap: { type: number; name: string } }
  | { attribute: { field: string; operator: 'exists' } }
  | { object_attribute: { field: string; operator: 'exists'; type_id: number } };

/** The people list's query encoding: JSON, percent-escaped, then base64. */
function encodeFilters(conditions: CountCondition[]): string {
  return btoa(encodeURIComponent(JSON.stringify({ and: conditions })));
}

/**
 * Everyone who has performed an event, or has an attribute at all.
 *
 * An event is addressed by the bitmap the backend indexes it under rather
 * than by name; an attribute is a plain existence check.
 */
export function peopleCountFilters(kind: DataIndexItemKind, name: string): string {
  if (kind === 'event') {
    return encodeFilters([
      {
        named_bitmap: {
          type: MESSAGE_BITMAP_TYPE,
          name: bytesToBase64(`${EVENT_BITMAP_PREFIX}-${name.replace('_*', '')}`),
        },
      },
    ]);
  }

  return encodeFilters([{ attribute: { field: name, operator: 'exists' } }]);
}

/** Every object of a type that has the attribute at all. */
export function objectsCountFilters(name: string, objectTypeId: number): string {
  return encodeFilters([{ object_attribute: { field: name, operator: 'exists', type_id: objectTypeId } }]);
}
